/**
 * Visual Table Editor Component
 * Supports paste from Excel, manual cell editing, and keyboard navigation.
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Plus, Trash2, Clipboard, Table2, AlertCircle } from 'lucide-react';
import InlineManufacturerAutocomplete from './InlineManufacturerAutocomplete';
import InlineUOMAutocomplete from './InlineUOMAutocomplete';

const COLUMNS = [
  { key: 'manufacturer', label: 'Manufacturer', required: true, width: '20%' },
  { key: 'mpn', label: 'MPN / Part Number', required: true, width: '20%' },
  { key: 'description', label: 'Description', required: true, width: '45%' },
  { key: 'uom', label: 'UOM', required: false, width: '10%', default: 'EA' },
];

const createEmptyRow = () => ({
  manufacturer: '',
  mpn: '',
  description: '',
  uom: 'EA',
});

export const VisualTableEditor = ({ onDataChange, initialData = [] }) => {
  const [mode, setMode] = useState('table'); // 'paste' or 'table'
  const [rows, setRows] = useState(initialData.length > 0 ? initialData : [createEmptyRow()]);
  const [pasteText, setPasteText] = useState('');
  const [parseError, setParseError] = useState('');
  const [focusedCell, setFocusedCell] = useState({ row: 0, col: 0 });
  
  const textareaRef = useRef(null);
  const tableRef = useRef(null);
  const cellRefs = useRef({});
  
  // Notify parent of data changes
  useEffect(() => {
    const validRows = rows.filter(row => 
      row.manufacturer.trim() && row.mpn.trim() && row.description.trim()
    );
    onDataChange(validRows);
  }, [rows, onDataChange]);
  
  // Parse a single row handling quoted values (defined before parseText that uses it)
  const parseRow = useCallback((line, delimiter) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current);
    
    return values.map(v => v.replace(/^"|"$/g, '').trim());
  }, []);
  
  // Parse pasted text (handles CSV, TSV, Excel paste) - used in Paste Data textarea mode
  const parseText = useCallback((text, allowCommaDelimiter = true) => {
    setParseError('');
    
    if (!text.trim()) {
      return [];
    }
    
    const lines = text.trim().split('\n');
    if (lines.length === 0) return [];
    
    // Detect delimiter (tab takes priority for Excel compatibility)
    // Only use comma as delimiter if explicitly allowed (Paste Data mode)
    const hasTab = lines[0].includes('\t');
    const hasComma = lines[0].includes(',');
    
    let delimiter;
    if (hasTab) {
      delimiter = '\t';
    } else if (hasComma && allowCommaDelimiter) {
      delimiter = ',';
    } else {
      // No valid delimiter found - this is single-cell text
      return [];
    }
    
    // Check if first line is a header
    const firstLineLower = lines[0].toLowerCase();
    const isHeader = 
      firstLineLower.includes('manufacturer') || 
      firstLineLower.includes('mpn') || 
      firstLineLower.includes('description') ||
      firstLineLower.includes('part') ||
      firstLineLower.includes('mfr');
    
    const dataLines = isHeader ? lines.slice(1) : lines;
    
    // Parse header for column mapping if present
    let columnMap = { manufacturer: 0, mpn: 1, description: 2, uom: 3 };
    
    if (isHeader) {
      const headers = parseRow(lines[0], delimiter);
      headers.forEach((header, index) => {
        const h = header.toLowerCase().trim();
        if (['manufacturer', 'mfr', 'mfg', 'vendor', 'brand', 'make'].includes(h)) {
          columnMap.manufacturer = index;
        } else if (['mpn', 'part_number', 'part_no', 'partnumber', 'pn', 'sku', 'item_number', 'part number'].includes(h)) {
          columnMap.mpn = index;
        } else if (['description', 'desc', 'name', 'title', 'part_description'].includes(h)) {
          columnMap.description = index;
        } else if (['uom', 'unit', 'unit_of_measure', 'units'].includes(h)) {
          columnMap.uom = index;
        }
      });
    }
    
    const parsedRows = [];
    for (const line of dataLines) {
      if (!line.trim()) continue;
      
      const values = parseRow(line, delimiter);
      if (values.length < 3) continue;
      
      parsedRows.push({
        manufacturer: (values[columnMap.manufacturer] || '').trim(),
        mpn: (values[columnMap.mpn] || '').trim(),
        description: (values[columnMap.description] || '').trim(),
        uom: (values[columnMap.uom] || 'EA').trim() || 'EA',
      });
    }
    
    return parsedRows;
  }, [parseRow]);
  
  // Update a single cell (defined before handleTablePaste that uses it)
  const updateCell = useCallback((rowIndex, key, value) => {
    setRows(prev => {
      const newRows = [...prev];
      newRows[rowIndex] = { ...newRows[rowIndex], [key]: value };
      return newRows;
    });
  }, []);
  
  // Handle paste in textarea mode
  const handlePasteTextChange = (e) => {
    const text = e.target.value;
    setPasteText(text);
    
    try {
      const parsed = parseText(text);
      if (parsed.length > 0) {
        setRows(parsed);
        setParseError('');
      } else if (text.trim()) {
        setParseError('Could not parse data. Ensure you have at least manufacturer, MPN, and description columns.');
      }
    } catch (err) {
      setParseError('Error parsing data: ' + err.message);
    }
  };
  
  // Handle Tab key in textarea to insert actual tab
  const handleTextareaKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;
      const newText = pasteText.substring(0, start) + '\t' + pasteText.substring(end);
      setPasteText(newText);
      
      // Set cursor position after tab
      setTimeout(() => {
        e.target.selectionStart = e.target.selectionEnd = start + 1;
      }, 0);
    }
  };
  
  // Handle paste directly into table
  // IMPORTANT: Only use TAB as delimiter, NOT comma
  // This prevents text like "Heat exchanger, Model ABC, 25 plates" from being split
  const handleTablePaste = useCallback((e) => {
    const text = e.clipboardData.getData('text');
    if (!text) return;
    
    e.preventDefault();
    
    // Check if this looks like structured data (has tabs = from Excel/Sheets)
    const hasTab = text.includes('\t');
    const hasMultipleLines = text.trim().split('\n').length > 1;
    
    // Only parse as structured data if it has tabs (Excel/Sheets paste)
    // Comma-separated text should NOT be parsed here - it goes to single cell
    if (hasTab || hasMultipleLines) {
      const parsed = parseText(text, false); // false = don't use comma as delimiter
      if (parsed.length > 0) {
        // If pasting multiple rows, replace from focused row
        const newRows = [...rows];
        const startRow = focusedCell.row;
        
        parsed.forEach((parsedRow, index) => {
          const targetRow = startRow + index;
          if (targetRow < newRows.length) {
            newRows[targetRow] = parsedRow;
          } else {
            newRows.push(parsedRow);
          }
        });
        
        setRows(newRows);
        return;
      }
    }
    
    // Single value paste into current cell (default behavior)
    // This handles text with commas correctly - keeps it as one value
    const colKey = COLUMNS[focusedCell.col]?.key;
    if (colKey) {
      updateCell(focusedCell.row, colKey, text.trim());
    }
  }, [focusedCell, rows, parseText, updateCell]);
  
  // Add a new empty row
  const addRow = () => {
    setRows(prev => [...prev, createEmptyRow()]);
    // Focus the first cell of the new row
    setTimeout(() => {
      const newRowIndex = rows.length;
      setFocusedCell({ row: newRowIndex, col: 0 });
      const cellKey = `${newRowIndex}-0`;
      cellRefs.current[cellKey]?.focus();
    }, 0);
  };
  
  // Delete a row
  const deleteRow = (index) => {
    if (rows.length <= 1) {
      // Don't delete last row, just clear it
      setRows([createEmptyRow()]);
    } else {
      setRows(prev => prev.filter((_, i) => i !== index));
    }
  };
  
  // Handle keyboard navigation in table
  const handleCellKeyDown = (e, rowIndex, colIndex) => {
    const key = COLUMNS[colIndex]?.key;
    
    switch (e.key) {
      case 'Tab':
        e.preventDefault();
        if (e.shiftKey) {
          // Move backwards
          if (colIndex > 0) {
            moveFocus(rowIndex, colIndex - 1);
          } else if (rowIndex > 0) {
            moveFocus(rowIndex - 1, COLUMNS.length - 1);
          }
        } else {
          // Move forwards
          if (colIndex < COLUMNS.length - 1) {
            moveFocus(rowIndex, colIndex + 1);
          } else if (rowIndex < rows.length - 1) {
            moveFocus(rowIndex + 1, 0);
          } else {
            // At last cell of last row, add new row
            addRow();
          }
        }
        break;
        
      case 'Enter':
        e.preventDefault();
        if (rowIndex < rows.length - 1) {
          moveFocus(rowIndex + 1, colIndex);
        } else {
          addRow();
        }
        break;
        
      case 'ArrowUp':
        if (rowIndex > 0) {
          e.preventDefault();
          moveFocus(rowIndex - 1, colIndex);
        }
        break;
        
      case 'ArrowDown':
        if (rowIndex < rows.length - 1) {
          e.preventDefault();
          moveFocus(rowIndex + 1, colIndex);
        }
        break;
        
      case 'Escape':
        e.target.blur();
        break;
    }
  };
  
  const moveFocus = (row, col) => {
    setFocusedCell({ row, col });
    const cellKey = `${row}-${col}`;
    cellRefs.current[cellKey]?.focus();
  };
  
  // Count valid rows
  const validCount = rows.filter(row => 
    row.manufacturer.trim() && row.mpn.trim() && row.description.trim()
  ).length;
  
  return (
    <div className="space-y-4">
      {/* Mode Toggle Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setMode('table')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
            mode === 'table'
              ? 'bg-blue-600 text-white'
              : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
          }`}
        >
          <Table2 className="w-4 h-4" />
          Table Editor
        </button>
        <button
          onClick={() => setMode('paste')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
            mode === 'paste'
              ? 'bg-blue-600 text-white'
              : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
          }`}
        >
          <Clipboard className="w-4 h-4" />
          Paste Data
        </button>
      </div>
      
      {/* Paste Mode */}
      {mode === 'paste' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <span>Paste from Excel, Google Sheets, or type tab-separated data.</span>
            <span className="text-slate-500">Press Tab key to insert tabs.</span>
          </div>
          <textarea
            ref={textareaRef}
            value={pasteText}
            onChange={handlePasteTextChange}
            onKeyDown={handleTextareaKeyDown}
            placeholder={`Manufacturer\tMPN\tDescription\tUOM
Danfoss\tSH300A3ABA\tScroll compressor 3HP R410A\tEA
Siemens\t6ES7511-1AK02\tCPU 1511-1 PN PLC controller\tEA

Or paste directly from Excel...`}
            className="w-full h-40 px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-slate-200 font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-slate-500 resize-none"
            data-testid="bulk-paste-textarea"
          />
          
          {parseError && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {parseError}
            </div>
          )}
          
          {pasteText && validCount > 0 && (
            <div className="text-sm text-emerald-400">
              ✓ {validCount} valid row{validCount !== 1 ? 's' : ''} parsed
            </div>
          )}
        </div>
      )}
      
      {/* Table Editor Mode */}
      {mode === 'table' && (
        <div className="space-y-3">
          <div className="text-sm text-slate-400">
            Click cells to edit. Paste from Excel directly into table. Tab/Enter to navigate.
          </div>
          
          <div 
            ref={tableRef}
            onPaste={handleTablePaste}
            className="border border-slate-700 rounded-xl overflow-hidden"
          >
            {/* Fixed Header Row */}
            <div className="bg-slate-800 border-b border-slate-600 sticky top-0 z-10">
              <div className="flex">
                {COLUMNS.map((col, idx) => (
                  <div
                    key={col.key}
                    className="px-3 py-2.5 text-xs font-semibold text-slate-300 uppercase tracking-wider"
                    style={{ width: col.width }}
                  >
                    <div className="flex items-center gap-1">
                      {col.label}
                      {col.required && <span className="text-red-400">*</span>}
                    </div>
                  </div>
                ))}
                <div className="w-12 px-2 py-2.5" /> {/* Delete button column */}
              </div>
            </div>
            
            {/* Data Rows */}
            <div className="max-h-64 overflow-y-auto bg-slate-900/50">
              {rows.map((row, rowIndex) => {
                const isComplete = row.manufacturer.trim() && row.mpn.trim() && row.description.trim();
                
                return (
                  <div
                    key={rowIndex}
                    className={`flex border-b border-slate-800 last:border-b-0 group ${
                      isComplete ? 'bg-emerald-500/5' : ''
                    }`}
                  >
                    {COLUMNS.map((col, colIndex) => {
                      const cellKey = `${rowIndex}-${colIndex}`;
                      const isEmpty = !row[col.key]?.trim();
                      const isRequired = col.required && isEmpty;
                      
                      // Manufacturer column gets special autocomplete treatment
                      if (col.key === 'manufacturer') {
                        return (
                          <div
                            key={col.key}
                            className="relative"
                            style={{ width: col.width }}
                          >
                            <InlineManufacturerAutocomplete
                              value={row[col.key]}
                              onChange={(value) => updateCell(rowIndex, col.key, value)}
                              onFocus={() => setFocusedCell({ row: rowIndex, col: colIndex })}
                              onKeyDown={(e) => handleCellKeyDown(e, rowIndex, colIndex)}
                              cellRef={(el) => cellRefs.current[cellKey] = el}
                              placeholder=""
                              className={`w-full px-3 py-2.5 bg-transparent text-sm text-slate-200 border-r border-slate-800 
                                focus:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-blue-500
                                placeholder-slate-600 transition-colors
                                ${isRequired ? 'bg-red-500/5' : ''}
                                ${focusedCell.row === rowIndex && focusedCell.col === colIndex ? 'bg-slate-800' : ''}
                              `}
                              testId={`cell-${rowIndex}-${col.key}`}
                            />
                          </div>
                        );
                      }
                      
                      // UOM column uses rich combobox with search and custom entry
                      if (col.key === 'uom') {
                        return (
                          <div
                            key={col.key}
                            className="relative"
                            style={{ width: col.width }}
                          >
                            <InlineUOMAutocomplete
                              value={row[col.key]}
                              onChange={(value) => updateCell(rowIndex, col.key, value)}
                              onFocus={() => setFocusedCell({ row: rowIndex, col: colIndex })}
                              onKeyDown={(e) => handleCellKeyDown(e, rowIndex, colIndex)}
                              cellRef={(el) => cellRefs.current[cellKey] = el}
                              placeholder="EA"
                              className={`w-full px-2 py-2.5 bg-transparent text-sm text-slate-200 border-r border-slate-800 
                                focus:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-blue-500
                                cursor-pointer transition-colors
                                ${focusedCell.row === rowIndex && focusedCell.col === colIndex ? 'bg-slate-800' : ''}
                              `}
                              testId={`cell-${rowIndex}-${col.key}`}
                            />
                          </div>
                        );
                      }
                      
                      // Other columns use standard input
                      return (
                        <div
                          key={col.key}
                          className="relative"
                          style={{ width: col.width }}
                        >
                          <input
                            ref={el => cellRefs.current[cellKey] = el}
                            type="text"
                            value={row[col.key]}
                            onChange={(e) => updateCell(rowIndex, col.key, e.target.value)}
                            onFocus={() => setFocusedCell({ row: rowIndex, col: colIndex })}
                            onKeyDown={(e) => handleCellKeyDown(e, rowIndex, colIndex)}
                            placeholder={col.default || ''}
                            className={`w-full px-3 py-2.5 bg-transparent text-sm text-slate-200 border-r border-slate-800 
                              focus:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-blue-500
                              placeholder-slate-600 transition-colors
                              ${isRequired ? 'bg-red-500/5' : ''}
                              ${focusedCell.row === rowIndex && focusedCell.col === colIndex ? 'bg-slate-800' : ''}
                            `}
                            data-testid={`cell-${rowIndex}-${col.key}`}
                          />
                        </div>
                      );
                    })}
                    
                    {/* Delete Row Button */}
                    <div className="w-12 flex items-center justify-center">
                      <button
                        onClick={() => deleteRow(rowIndex)}
                        className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded opacity-0 group-hover:opacity-100 transition-all"
                        title="Delete row"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Add Row Button & Count */}
          <div className="flex items-center justify-between">
            <button
              onClick={addRow}
              className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Row
            </button>
            
            <div className="text-sm">
              {validCount > 0 ? (
                <span className="text-emerald-400">
                  {validCount} part{validCount !== 1 ? 's' : ''} ready
                </span>
              ) : (
                <span className="text-slate-500">
                  Fill required fields (Manufacturer, MPN, Description)
                </span>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Preview Table (shows in both modes when there's data) */}
      {validCount > 0 && mode === 'paste' && (
        <div className="border border-slate-700 rounded-xl overflow-hidden mt-4">
          <div className="bg-slate-800/50 px-4 py-2 border-b border-slate-700">
            <p className="text-sm font-medium text-slate-300">Preview ({validCount} rows)</p>
          </div>
          <div className="max-h-48 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-800/30 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-400 uppercase w-8">#</th>
                  {COLUMNS.map(col => (
                    <th key={col.key} className="px-3 py-2 text-left text-xs font-semibold text-slate-400 uppercase">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {rows.filter(r => r.manufacturer.trim() && r.mpn.trim() && r.description.trim()).slice(0, 10).map((row, i) => (
                  <tr key={i} className="hover:bg-slate-800/30">
                    <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                    <td className="px-3 py-2 text-white font-medium">{row.manufacturer}</td>
                    <td className="px-3 py-2 text-blue-400 font-mono">{row.mpn}</td>
                    <td className="px-3 py-2 text-slate-300 truncate max-w-xs">{row.description}</td>
                    <td className="px-3 py-2 text-slate-400">{row.uom}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {validCount > 10 && (
              <div className="px-4 py-2 bg-slate-800/30 border-t border-slate-700 text-sm text-slate-500">
                ... and {validCount - 10} more rows
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VisualTableEditor;
